import { useChildrenContext } from "@/context/children";
import { useAlert } from "@/hooks/useAlert";
import {
  BillingLastInvoiceResponseDto,
  CellularInfoResponse,
  CellularPortInResponseDto,
  CellularPortOutCredentialsDto,
  CellularPortOutResponseDto,
  CellularProviderResponseDto,
  ChildSubscriptionType,
  CreatePortInRequestDto,
  InvitationResponseDto,
  PortOutBehaviorEnum,
  PortingBehaviorEnum,
  SharedChildPermissionDto,
  SubscriptionAddOnResponseDto,
  BillingAddressResponse,
} from "@/openapi";
import { NotificationSettingsResponseDto } from "@/openapi/models/notification-settings-response-dto";
import { UpdateNotificationSettingsDto } from "@/openapi/models/update-notification-settings-dto";
import { UpdatePortingRequestDto } from "@/openapi/models/update-porting-request-dto";
import { getCaregiverService } from "@/util/api-helper";
import { fetchCellularInfoById } from "@/util/cellular-helper";
import { useChild } from "@/util/child-helper";
import { handleApiError } from "@/util/error-handlers";
import { useGetChildFieldXDeiviceInfo } from "@/util/fieldx-helper";
import { useCaregiver, useMe, useMeContext } from "@/util/me-context";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const useNotificationSettings = () => {
  const alert = useAlert();
  const child = useChild();
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettingsResponseDto>();
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const fetchNotificationSettings = useCallback(async () => {
    setLoading(true);
    try {
      const caregiverService = await getCaregiverService();
      const response = await caregiverService.getNotificationSettings();
      setNotificationSettings(response.data);
    } catch (e) {
      handleApiError(e);
      alert.error("Failed to fetch notification settings");
    } finally {
      setLoading(false);
    }
  }, [alert]);

  const updateNotificationSettings = useCallback(async (notificationSettings: UpdateNotificationSettingsDto) => {
    setUpdating(true);
    try {
      const caregiverService = await getCaregiverService();
      await caregiverService.updateNotificationSettings(notificationSettings);
      setNotificationSettings({
        ...notificationSettings,
        appRequests: notificationSettings.appRequests ?? false,
      } as NotificationSettingsResponseDto);
    } catch (e) {
      handleApiError(e);
      throw e;
    } finally {
      setUpdating(false);
    }
  }, []);

  useEffect(() => {
    if (child) {
      (async () => {
        await fetchNotificationSettings();
      })();
    }
  }, [child, fetchNotificationSettings]);

  return useMemo(
    () => ({ notificationSettings, loading, updateNotificationSettings, updating }),
    [notificationSettings, loading, updateNotificationSettings, updating],
  );
};

export const useIsCellularActivated = (forceRefresh = false) => {
  const child = useChild();
  const { refetch: refetchChildren } = useChildrenContext();
  const alreadyRefreshed = useRef(false);

  useEffect(() => {
    if (child && !child.cellularExternalSubId && forceRefresh && !alreadyRefreshed.current) {
      alreadyRefreshed.current = true;
      (async () => {
        await refetchChildren();
      })();
    }
  }, [child, forceRefresh, refetchChildren]);

  return useMemo(() => !!child?.cellularExternalSubId, [child?.cellularExternalSubId]);
};

export const useHasCellularSubscription = () => {
  const { data: me } = useMeContext();
  const caregiver = useCaregiver();
  const child = useChild();

  const hasCellularSubscription = useMemo(() => {
    const childAlreadyHasCellularSub =
      child?.subscriptionTypes?.includes(ChildSubscriptionType.Cellular) || child?.subscriptionTypes?.includes(ChildSubscriptionType.Watch);

    const hasExtraCellularSub = caregiver.cellularSubCount > caregiver.cellularChildCount;

    const isPhoneChild = child?.subscriptionTypes?.includes(ChildSubscriptionType.Phone);
    return (
      childAlreadyHasCellularSub ||
      (hasExtraCellularSub && isPhoneChild) ||
      me?.attributes.email?.endsWith("@pinwheel.com") ||
      caregiver.billingEmail?.endsWith("@pinwheel.com")
    );
  }, [caregiver.billingEmail, caregiver.cellularChildCount, caregiver.cellularSubCount, child?.subscriptionTypes, me?.attributes.email]);

  return useMemo(() => hasCellularSubscription, [hasCellularSubscription]);
};

export const useCellularActivation = () => {
  const [loading, setLoading] = useState(false);

  const activateCellular = useCallback(async (childId: string, iccid: string) => {
    try {
      setLoading(true);
      const caregiverService = await getCaregiverService();
      const res = await caregiverService.activateCellular({ childId, iccid });
      return res;
    } catch (err) {
      handleApiError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return useMemo(() => ({ activateCellular, loading }), [activateCellular, loading]);
};

export const useGetCellularInfo = () => {
  const alert = useAlert();
  const child = useChild();
  const [loading, setLoading] = useState(false);
  const [cellularInfo, setCellularInfo] = useState<CellularInfoResponse>();

  const fetchCellularInfo = useCallback(async () => {
    if (!child) return;
    setLoading(true);
    try {
      const data = await fetchCellularInfoById(child.id);
      setCellularInfo(data);
      return data;
    } catch (err) {
      handleApiError(err);
      alert.error("Failed to fetch cellular info");
    } finally {
      setLoading(false);
    }
  }, [alert, child]);

  useEffect(() => {
    if (child && child.cellularExternalSubId) {
      fetchCellularInfo();
    }
  }, [child, fetchCellularInfo]);

  return useMemo(() => ({ fetchCellularInfo, loading, cellularInfo }), [fetchCellularInfo, loading, cellularInfo]);
};

export const useCancelPortIn = () => {
  const [loading, setLoading] = useState(false);

  const cancelPortIn = useCallback(async (portInId: string, childId: string) => {
    setLoading(true);
    try {
      const caregiverService = await getCaregiverService();
      const response = await caregiverService.cancelPortIn(portInId, childId);
      return response.data;
    } catch (error) {
      handleApiError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return useMemo(() => ({ cancelPortIn, loading }), [cancelPortIn, loading]);
};

export const useGetLastCellularInvoice = (enable = true) => {
  const child = useChild();
  const alert = useAlert();
  const [loading, setLoading] = useState(false);
  const [cellularInvoice, setCellularInvoice] = useState<BillingLastInvoiceResponseDto>();

  const fetchCellularInvoice = useCallback(
    async (childId: string) => {
      setLoading(true);
      try {
        const caregiverService = await getCaregiverService();
        const response = await caregiverService.getLastCellularInvoice(childId);
        setCellularInvoice(response.data);
      } catch (err) {
        handleApiError(err);
        alert.error("Failed to fetch cellular invoice");
      } finally {
        setLoading(false);
      }
    },
    [alert],
  );

  useEffect(() => {
    if (enable && child) {
      fetchCellularInvoice(child.id);
    }
  }, [child, enable, fetchCellularInvoice]);

  return useMemo(() => ({ fetchCellularInvoice, loading, cellularInvoice }), [fetchCellularInvoice, loading, cellularInvoice]);
};

export const useValidateCellularIccid = () => {
  const child = useChild();
  const [loading, setLoading] = useState(false);
  const { refetch: refetchChildren } = useChildrenContext();
  const { getDeviceData } = useGetChildFieldXDeiviceInfo();

  const validateChildIccid = useCallback(async () => {
    try {
      setLoading(true);
      const refreshedChildren = await refetchChildren();

      if (!refreshedChildren) {
        return {
          isValid: false,
          error: "Failed to refresh child information",
        };
      }

      if (!child) {
        return {
          isValid: false,
          error: "Child information not found",
        };
      }

      const freshChild = refreshedChildren.find((c) => c.id === child.id);

      if (!freshChild) {
        return {
          isValid: false,
          error: "Failed to find child information",
        };
      }

      const deviceId = freshChild.deviceInfos[0]?.deviceId;
      if (!deviceId) {
        return {
          isValid: false,
          error: "Device information not found",
        };
      }
      const deviceData = await getDeviceData(deviceId);
      if (!deviceData) {
        return {
          isValid: false,
          error: "Failed to retrieve device data",
        };
      }

      const iccid = deviceData?.networkInfo?.sim1?.mIccId;
      if (!iccid) {
        return {
          isValid: false,
          error:
            "We couldn't retrieve the SIM card information for this device yet. Please insert your SIM card into your device and restart your device to start your cellular activation.",
        };
      }

      return { isValid: true, iccid };
    } catch (err) {
      return {
        isValid: false,
        error: "Validation failed due to an unexpected error",
      };
    } finally {
      setLoading(false);
    }
  }, [child, getDeviceData, refetchChildren]);

  return useMemo(
    () => ({
      validateChildIccid,
      loading,
    }),
    [validateChildIccid, loading],
  );
};

export const useGetCellularPortOuts = (childId?: string) => {
  const [loading, setLoading] = useState(false);
  const [portOuts, setPortOuts] = useState<CellularPortOutResponseDto[]>([]);

  const fetchPortOuts = useCallback(async (childId?: string) => {
    setLoading(true);
    try {
      const caregiverService = await getCaregiverService();
      const response = await caregiverService.getPortOutRequest(childId);
      setPortOuts(response.data);
      return response.data;
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPortOuts(childId);
  }, [childId, fetchPortOuts]);

  return useMemo(
    () => ({
      fetchPortOuts,
      loading,
      portOuts,
    }),
    [fetchPortOuts, loading, portOuts],
  );
};

export const useCellularPortOut = () => {
  const [loading, setLoading] = useState(false);
  const alert = useAlert();

  const portOut = useCallback(
    async (childId: string, behavior?: PortOutBehaviorEnum) => {
      setLoading(true);
      try {
        const caregiverService = await getCaregiverService();
        const res = await caregiverService.portOut({ childId, behavior });
        return res;
      } catch (err) {
        handleApiError(err);
        alert.error("Failed to send cellular port out request");
      } finally {
        setLoading(false);
      }
    },
    [alert],
  );

  return useMemo(() => ({ portOut, loading }), [portOut, loading]);
};

export const useRetrieveCellularPortOutCredentials = () => {
  const [loading, setLoading] = useState(false);
  const [credential, setCredential] = useState<CellularPortOutCredentialsDto>();
  const alert = useAlert();

  const retrievePortOutCredentials = useCallback(
    async (portId: string) => {
      setLoading(true);
      try {
        const caregiverService = await getCaregiverService();
        const res = await caregiverService.getPortOutCredentials(portId);
        setCredential(res.data);
        return res;
      } catch (err) {
        handleApiError(err);
        alert.error("Failed to retrieve cellular port out credentials");
      } finally {
        setLoading(false);
      }
    },
    [alert],
  );

  return useMemo(() => ({ retrievePortOutCredentials, loading, credential }), [retrievePortOutCredentials, loading, credential]);
};

export const useCaregiverInvites = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [caregiverInvites, setCaregiverInvites] = useState<InvitationResponseDto[]>([]);

  const fetchCaregiverInvites = useCallback(async () => {
    setIsLoading(true);
    try {
      const caregiverService = await getCaregiverService();
      const response = await caregiverService.getInvitations();
      setCaregiverInvites(response.data);
      return response.data;
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await fetchCaregiverInvites();
    })();
  }, [fetchCaregiverInvites]);

  return useMemo(() => ({ caregiverInvites, isLoading, fetchCaregiverInvites }), [caregiverInvites, isLoading, fetchCaregiverInvites]);
};

export const useUpdateInvitation = () => {
  const [loading, setLoading] = useState(false);

  const updateInvitation = useCallback(async (invitationId: number, sharedChildrenPermissions: SharedChildPermissionDto[]) => {
    setLoading(true);
    try {
      const caregiverService = await getCaregiverService();
      const response = await caregiverService.updateInvitation(invitationId, { sharedChildrenPermissions });
      return response.data;
    } catch (err) {
      handleApiError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return useMemo(() => ({ updateInvitation, loading }), [updateInvitation, loading]);
};

export const useInviteCaregiver = () => {
  const [loading, setLoading] = useState(false);

  const inviteCaregiver = useCallback(
    async (email: string, name: string, childIds: string[], sharedChildrenPermissions: SharedChildPermissionDto[] = []) => {
      setLoading(true);
      try {
        const caregiverService = await getCaregiverService();
        return (
          await caregiverService.createInvitation({
            recipientEmail: email,
            recipientName: name,
            sharedChildrenIds: childIds,
            sharedChildrenPermissions,
          })
        ).data;
      } catch (err) {
        handleApiError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return useMemo(() => ({ inviteCaregiver, loading }), [inviteCaregiver, loading]);
};

export const useRemoveCaregiverInvite = () => {
  const [loading, setLoading] = useState(false);

  const removeCaregiverInvite = useCallback(async (email: string, childIds: string[]) => {
    setLoading(true);
    try {
      const caregiverService = await getCaregiverService();
      return await caregiverService.removeInvitation({
        recipientEmail: email,
        sharedChildrenIds: childIds,
      });
    } catch (err) {
      handleApiError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return useMemo(() => ({ removeCaregiverInvite, loading }), [removeCaregiverInvite, loading]);
};

export const useIsSecondaryCaregiver = () => {
  const me = useMe();
  const child = useChild();

  return useMemo(() => {
    if (!me?.attributes.sub || !child?.caregiverId) return false;
    return me.attributes.sub !== child.caregiverId;
  }, [me?.attributes.sub, child?.caregiverId]);
};

export const useInviteCaregiverFinalize = () => {
  const [loading, setLoading] = useState(false);

  const finalizeInvite = useCallback(async () => {
    setLoading(true);
    try {
      const caregiverService = await getCaregiverService();
      return await caregiverService.finalizeInvitation();
    } catch (err) {
      handleApiError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return useMemo(() => ({ finalizeInvite, loading }), [finalizeInvite, loading]);
};

export const useGetActiveSubscriptionAddOns = () => {
  const child = useChild();
  const [activeAddOns, setActiveAddOns] = useState<SubscriptionAddOnResponseDto[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchActiveAddOns = useCallback(async () => {
    if (!child?.id) return;
    setLoading(true);
    try {
      const caregiverService = await getCaregiverService();
      const response = await caregiverService.getActiveSubscriptionAddOns(child.id);
      setActiveAddOns(response.data);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  }, [child?.id]);

  useEffect(() => {
    fetchActiveAddOns();
  }, [fetchActiveAddOns]);

  return useMemo(() => ({ activeAddOns, loading, fetchActiveAddOns }), [activeAddOns, fetchActiveAddOns, loading]);
};

export const useCancelCellularService = () => {
  const [loading, setLoading] = useState(false);
  const { refetch } = useChildrenContext();
  const cancelCellularService = useCallback(
    async (childId: string) => {
      setLoading(true);
      try {
        const caregiverService = await getCaregiverService();
        const res = await caregiverService.cancelCellularService({ childId });
        await refetch();
        return res;
      } catch (err) {
        handleApiError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [refetch],
  );

  return useMemo(() => ({ cancelCellularService, loading }), [cancelCellularService, loading]);
};

const normalizePortingData = (
  childId: string,
  data?: BillingAddressResponse,
  behavior: PortingBehaviorEnum = PortingBehaviorEnum.PortIn,
): CreatePortInRequestDto => ({
  childId,
  firstName: data?.firstName || "",
  lastName: data?.lastName || "",
  address: {
    line1: data?.address?.line1 || "",
    line2: data?.address?.line2 || "",
    city: data?.address?.city || "",
    state: data?.address?.state || "",
    postalCode: data?.address?.postalCode || "",
    country: data?.address?.country || "",
  },
  iccid: "",
  accountNumber: "",
  accountPin: "",
  phoneNumber: "",
  donorProviderId: "",
  behavior,
});

export const useCellularPortInMultiStepForm = (
  childId: string,
  initialUserInfo?: BillingAddressResponse,
  behavior: PortingBehaviorEnum = PortingBehaviorEnum.PortIn,
) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<CreatePortInRequestDto>(normalizePortingData(childId, initialUserInfo, behavior));

  useEffect(() => {
    if (initialUserInfo) {
      setFormData(normalizePortingData(childId, initialUserInfo, behavior));
    }
  }, [initialUserInfo, childId, behavior]);

  const updateFormData = useCallback(
    (values: Partial<CreatePortInRequestDto> & { address?: Partial<CreatePortInRequestDto["address"]> }) => {
      setFormData((prev) => ({
        ...prev,
        ...values,
        address: {
          ...prev.address,
          ...(values.address || {}),
        },
      }));
    },
    [],
  );

  const nextStep = useCallback(
    (values?: Partial<CreatePortInRequestDto>) => {
      if (values) updateFormData(values);
      setActiveStep((prev) => prev + 1);
    },
    [updateFormData],
  );

  const prevStep = useCallback(() => {
    setActiveStep((prev) => Math.max(0, prev - 1));
  }, []);

  const goToStep = useCallback((step: number) => {
    setActiveStep(step);
  }, []);

  const resetForm = useCallback(() => {
    setActiveStep(0);
    setFormData(normalizePortingData(childId, initialUserInfo, behavior));
  }, [initialUserInfo, childId, behavior]);

  return useMemo(
    () => ({
      activeStep,
      formData,
      nextStep,
      prevStep,
      goToStep,
      updateFormData,
      resetForm,
    }),
    [activeStep, formData, nextStep, prevStep, goToStep, updateFormData, resetForm],
  );
};

export const useGetCellularUserInfo = () => {
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState<BillingAddressResponse | undefined>(undefined);

  const fetchCellularUserInfo = useCallback(async () => {
    setLoading(true);
    try {
      const caregiverService = await getCaregiverService();
      const response = await caregiverService.getBillingAddress();
      setUserInfo(response.data);
      return response.data;
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await fetchCellularUserInfo();
    })();
  }, [fetchCellularUserInfo]);

  return useMemo(() => ({ fetchCellularUserInfo, loading, userInfo }), [fetchCellularUserInfo, loading, userInfo]);
};

export const useGetCellularProviders = () => {
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState<CellularProviderResponseDto[]>([]);

  const fetchCellularProviders = useCallback(async () => {
    if (providers.length > 0) {
      return providers;
    }
    setLoading(true);
    try {
      const caregiverService = await getCaregiverService();
      const response = await caregiverService.getCellularProviders();
      const sortedProviders = response.data.sort((a, b) => a.name.localeCompare(b.name));
      setProviders(sortedProviders);
      return sortedProviders;

    } catch (err) {
      handleApiError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [providers]);

  return useMemo(() => ({ fetchCellularProviders, loading, providers }), [fetchCellularProviders, loading, providers]);
};

export const useCreatePortInRequest = () => {
  const [loading, setLoading] = useState(false);
  const { refetch } = useChildrenContext();

  const createPortInRequest = useCallback(async (data: CreatePortInRequestDto) => {
    setLoading(true);
    try {
      const caregiverService = await getCaregiverService();
      const response = await caregiverService.createPortIn(data);
      await refetch();
      return response.data;
    } catch (err) {
      handleApiError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [refetch]);

  return useMemo(() => ({ createPortInRequest, loading }), [createPortInRequest, loading]);
};

// Data mapping utilities for edit port-in functionality
const mapPortInResponseToCreateRequest = (
  portInData: CellularPortInResponseDto,
  childId: string,
  declinedCode?: string,
): CreatePortInRequestDto => ({
  childId,
  phoneNumber: portInData.phoneNumber,
  donorProviderId: portInData.donorProviderId || "",
  // Clear name fields if they're specifically the problem, otherwise preserve
  firstName: declinedCode === "portingFirstNameRequiredOrInvalid" ? "" : portInData.firstName,
  lastName: declinedCode === "portingLastNameRequiredOrInvalid" ? "" : portInData.lastName,
  address: {
    // Clear address fields if they're specifically the problem, otherwise preserve
    line1: declinedCode === "portingAddressRequiredOrInvalid" ? "" : portInData.address.line1,
    line2: portInData.address.line2 || "",
    city: declinedCode === "portingAddressRequiredOrInvalid" ? "" : portInData.address.city,
    state: declinedCode === "portingAddressRequiredOrInvalid" ? "" : (portInData.address.state || ""),
    postalCode: declinedCode === "portingPostalCodeRequiredOrInvalid" ? "" : portInData.address.postalCode,
    country: portInData.address.country,
  },
  // Clear account number if it's specifically the problem, otherwise preserve
  accountNumber: declinedCode === "portingAccountNumberRequiredOrInvalid" ? "" : (portInData.accountNumber || ""),
  // Clear PIN if it's specifically the problem, otherwise preserve existing PIN
  accountPin: declinedCode === "portingAccountPinRequiredOrInvalid" ? "" : portInData.accountPinExists ? "PIN_EXISTS" : "",
  behavior: portInData.behavior,
  iccid: "",
});

export const useGetPortIn = (portInId: string, childId: string) => {
  const [loading, setLoading] = useState(false);
  const [portInData, setPortInData] = useState<CellularPortInResponseDto | null>(null);
  const alert = useAlert();

  const fetchPortIn = useCallback(async () => {
    setLoading(true);
    try {
      const caregiverService = await getCaregiverService();
      const response = await caregiverService.getPortIn(portInId, childId);
      setPortInData(response.data);
      return response.data;
    } catch (err) {
      handleApiError(err);
      alert.error("Failed to fetch port-in information");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [portInId, childId, alert]);

  return useMemo(
    () => ({
      fetchPortIn,
      portInData,
      loading,
    }),
    [fetchPortIn, portInData, loading],
  );
};

export const useUpdatePortIn = (portInId: string, childId: string) => {
  const [updating, setUpdating] = useState(false);
  const { refetch } = useChildrenContext();

  const updatePortIn = useCallback(async (data: UpdatePortingRequestDto) => {
    setUpdating(true);
    try {
      const caregiverService = await getCaregiverService();
      const response = await caregiverService.updatePortIn(portInId, childId, data);
      await refetch();
      return response.data;
    } catch (err) {
      handleApiError(err);
      throw err;
    } finally {
      setUpdating(false);
    }
  }, [portInId, childId, refetch]);

  return useMemo(
    () => ({
      updatePortIn,
      updating,
    }),
    [updatePortIn, updating],
  );
};

export const useEditPortIn = (portInId: string, childId: string, declinedCode?: string) => {
  const { fetchPortIn, portInData, loading } = useGetPortIn(portInId, childId);
  const { updatePortIn, updating } = useUpdatePortIn(portInId, childId);

  const getFormData = useCallback(() => {
    if (!portInData) return null;
    return mapPortInResponseToCreateRequest(portInData, childId, declinedCode);
  }, [portInData, childId, declinedCode]);

  return useMemo(
    () => ({
      fetchPortIn,
      updatePortIn,
      getFormData,
      portInData,
      loading,
      updating,
    }),
    [fetchPortIn, updatePortIn, getFormData, portInData, loading, updating],
  );
};

export const useEditPortInMultiStepForm = (portInId: string, childId: string, declinedCode?: string) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<CreatePortInRequestDto | null>(null);
  const { fetchPortIn, updatePortIn, getFormData, portInData, loading, updating } = useEditPortIn(portInId, childId, declinedCode);

  useEffect(() => {
    const initialFormData = getFormData();
    if (initialFormData) {
      setFormData(initialFormData);
    }
  }, [getFormData]);

  const updateFormData = useCallback(
    (values: Partial<CreatePortInRequestDto> & { address?: Partial<CreatePortInRequestDto["address"]> }) => {
      if (!formData) return;
      setFormData((prev) => ({
        ...prev!,
        ...values,
        address: {
          ...prev!.address,
          ...(values.address || {}),
        },
      }));
    },
    [formData],
  );

  const nextStep = useCallback(
    (values?: Partial<CreatePortInRequestDto>) => {
      if (values) updateFormData(values);
      setActiveStep((prev) => prev + 1);
    },
    [updateFormData],
  );

  const prevStep = useCallback(() => {
    setActiveStep((prev) => Math.max(0, prev - 1));
  }, []);

  const goToStep = useCallback((step: number) => {
    setActiveStep(step);
  }, []);

  return useMemo(
    () => ({
      activeStep,
      formData,
      portInData,
      nextStep,
      prevStep,
      goToStep,
      updateFormData,
      fetchPortIn,
      updatePortIn,
      loading,
      updating,
    }),
    [activeStep, formData, portInData, nextStep, prevStep, goToStep, updateFormData, fetchPortIn, updatePortIn, loading, updating],
  );
};
